import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Activity, Shield, Rocket, Heart, CheckCircle, XCircle,
  AlertTriangle, RefreshCw, Loader2, Clock, Users,
  Database, Server, FileText, Trash2, Eye, Zap, Bot
} from 'lucide-react';
import { API } from '../config/api';
import { api } from '../utils/api';
import AutomationDashboard from '../components/admin/AutomationDashboard';

export default function AdminAutomationPage() {
  const { user, token, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role !== 'owner' && user.role !== 'admin') {
      navigate('/');
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-automation-page">
      <div className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
              <Activity className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">Automation Center</h1>
              <p className="text-sm text-muted-foreground">Health, Anti-Cheat, Boosts & System Status</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs defaultValue="health">
          <TabsList className="bg-card border border-border mb-6 flex-wrap p-1">
            <TabsTrigger value="health" className="data-[state=active]:bg-primary/20">
              <Heart className="w-4 h-4 mr-2" />
              Health
            </TabsTrigger>
            <TabsTrigger value="anticheat" className="data-[state=active]:bg-primary/20">
              <Shield className="w-4 h-4 mr-2" />
              Anti-Cheat
            </TabsTrigger>
            <TabsTrigger value="boosts" className="data-[state=active]:bg-primary/20">
              <Rocket className="w-4 h-4 mr-2" />
              Boosts
            </TabsTrigger>
            <TabsTrigger value="ai-insights" className="data-[state=active]:bg-primary/20">
              <Bot className="w-4 h-4 mr-2" />
              AI Insights
            </TabsTrigger>
          </TabsList>

          <TabsContent value="health"><HealthTab token={token} /></TabsContent>
          <TabsContent value="anticheat"><AntiCheatTab token={token} /></TabsContent>
          <TabsContent value="boosts"><BoostsAdminTab token={token} /></TabsContent>
          <TabsContent value="ai-insights"><AutomationDashboard /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ---- Health Tab ---- */
function HealthTab({ token }) {
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => { fetchHealth(); }, []);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const [latest, hist] = await Promise.all([
        api.get('/admin/health/latest'),
        api.get('/admin/health/history', { limit: 5 }),
      ]);
      if (latest.ok) setReport(latest.data);
      if (hist.ok) setHistory(hist.data?.reports || []);
    } catch { toast.error('Failed to fetch health data'); }
    finally { setLoading(false); }
  };

  const runHealthCheck = async () => {
    setRunning(true);
    try {
      const { ok, data } = await api.post('/admin/health/run');
      if (ok) {
        setReport(data);
        toast.success('Health check complete');
        fetchHealth();
      }
    } catch { toast.error('Health check failed'); }
    finally { setRunning(false); }
  };

  const statusIcon = (s) => {
    if (s === 'ok' || s === 'healthy') return <CheckCircle className="w-4 h-4 text-green-400" />;
    if (s === 'warning' || s === 'repaired') return <AlertTriangle className="w-4 h-4 text-amber-400" />;
    return <XCircle className="w-4 h-4 text-red-400" />;
  };

  const statusBadge = (s) => {
    const colors = {
      healthy: 'bg-green-500/20 text-green-400',
      repaired: 'bg-amber-500/20 text-amber-400',
      critical: 'bg-red-500/20 text-red-400',
    };
    return <Badge className={colors[s] || colors.healthy}>{s}</Badge>;
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="health-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-lg">Platform Health</h2>
        <Button onClick={runHealthCheck} disabled={running} size="sm" data-testid="run-health-check">
          {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Run Check
        </Button>
      </div>

      {report && !report.message ? (
        <>
          <Card className="bg-card border-border p-5" data-testid="health-report">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {statusIcon(report.status)}
                <span className="font-bold">Overall Status</span>
                {statusBadge(report.status)}
              </div>
              <span className="text-xs text-muted-foreground">{report.timestamp ? new Date(report.timestamp).toLocaleString() : ''}</span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.entries(report.checks || {}).map(([key, val]) => (
                <div key={key} className="flex items-center gap-2 p-3 rounded-lg bg-muted/20 border border-border">
                  {statusIcon(val.status)}
                  <div>
                    <p className="text-xs font-medium capitalize">{key.replace(/_/g, ' ')}</p>
                    {val.count !== undefined && <p className="text-[10px] text-muted-foreground">{val.count} issues</p>}
                    {val.message && <p className="text-[10px] text-muted-foreground">{val.message}</p>}
                  </div>
                </div>
              ))}
            </div>

            {report.auto_repairs?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-border">
                <h4 className="text-xs font-bold text-amber-400 mb-2">Auto-Repairs Performed</h4>
                {report.auto_repairs.map((r, i) => (
                  <p key={i} className="text-xs text-muted-foreground mb-1">- {r}</p>
                ))}
              </div>
            )}

            {report.critical_alerts?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-red-500/20">
                <h4 className="text-xs font-bold text-red-400 mb-2">Critical Alerts</h4>
                {report.critical_alerts.map((a, i) => (
                  <p key={i} className="text-xs text-red-300 mb-1">- {a}</p>
                ))}
              </div>
            )}
          </Card>
        </>
      ) : (
        <Card className="bg-card border-border p-6 text-center">
          <Server className="w-8 h-8 mx-auto mb-2 text-muted-foreground/80" />
          <p className="text-sm text-muted-foreground">No health report yet. Run a check to get started.</p>
        </Card>
      )}

      {history.length > 0 && (
        <Card className="bg-card border-border p-5" data-testid="health-history">
          <h3 className="text-sm font-bold mb-3">Recent History</h3>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded bg-muted/20">
                <div className="flex items-center gap-2">
                  {statusIcon(h.status)}
                  <span className="text-xs">{h.timestamp ? new Date(h.timestamp).toLocaleString() : 'Unknown'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {h.auto_repairs?.length > 0 && <Badge variant="outline" className="text-[10px]">{h.auto_repairs.length} repairs</Badge>}
                  {h.critical_alerts?.length > 0 && <Badge className="bg-red-500/20 text-red-400 text-[10px]">{h.critical_alerts.length} alerts</Badge>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ---- Anti-Cheat Tab ---- */
function AntiCheatTab({ token }) {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  useEffect(() => { fetchFlags(); }, []);

  const fetchFlags = async () => {
    setLoading(true);
    try {
      const { ok, data } = await api.get('/admin/anticheat/flags', { status: 'all', limit: 50 });
      if (ok) setFlags(data?.flags || []);
    } catch { toast.error('Failed to fetch flags'); }
    finally { setLoading(false); }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const { ok, data } = await api.post('/admin/anticheat/scan');
      if (ok) {
        toast.success(`Scan complete: ${data?.flagged} items flagged`);
        fetchFlags();
      }
    } catch { toast.error('Scan failed'); }
    finally { setScanning(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const activeFlags = flags.filter(f => !f.resolved);
  const resolvedFlags = flags.filter(f => f.resolved);

  return (
    <div className="space-y-4" data-testid="anticheat-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-lg">Anti-Manipulation</h2>
        <Button onClick={runScan} disabled={scanning} size="sm" data-testid="run-anticheat-scan">
          {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Shield className="w-4 h-4 mr-1" />}
          Run Scan
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-red-500/5 border-red-500/20 p-4 text-center">
          <p className="text-3xl font-bold text-red-400">{activeFlags.length}</p>
          <p className="text-xs text-muted-foreground">Active Flags</p>
        </Card>
        <Card className="bg-green-500/5 border-green-500/20 p-4 text-center">
          <p className="text-3xl font-bold text-green-400">{resolvedFlags.length}</p>
          <p className="text-xs text-muted-foreground">Resolved</p>
        </Card>
      </div>

      {/* Active Flags */}
      {activeFlags.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-xs text-muted-foreground uppercase tracking-wide font-bold">Active Flags</h4>
          {activeFlags.map((flag, i) => (
            <Card key={i} className="bg-red-500/5 border-red-500/20 p-4" data-testid={`flag-${i}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-red-500/20 text-red-400 text-[10px]">{flag.type?.replace(/_/g, ' ')}</Badge>
                    {flag.user_handle && <span className="text-xs text-muted-foreground">@{flag.user_handle}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    {flag.count && <p>Count: {flag.count} (threshold: {flag.threshold})</p>}
                    {flag.multiplier && <p>Spike: {flag.multiplier}x normal rate</p>}
                    {flag.target_id && <p>Target: {flag.target_id}</p>}
                  </div>
                </div>
                <span className="text-[10px] text-muted-foreground">{flag.detected_at ? new Date(flag.detected_at).toLocaleString() : ''}</span>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-card border-border p-6 text-center" data-testid="no-flags">
          <Shield className="w-8 h-8 mx-auto mb-2 text-green-500" />
          <p className="text-sm text-muted-foreground">No active manipulation flags</p>
          <p className="text-[10px] text-muted-foreground/80">Platform is clean</p>
        </Card>
      )}
    </div>
  );
}

/* ---- Boosts Admin Tab ---- */
function BoostsAdminTab({ token }) {
  const [boosts, setBoosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');

  useEffect(() => { fetchBoosts(); }, [statusFilter]);

  const fetchBoosts = async () => {
    setLoading(true);
    try {
      const { ok, data } = await api.get('/admin/boost/all', { status: statusFilter });
      if (ok) setBoosts(data?.boosts || []);
    } catch { toast.error('Failed to fetch boosts'); }
    finally { setLoading(false); }
  };

  const handleAction = async (boostId, action) => {
    try {
      const { ok } = await api.post(`/admin/boost/${boostId}/${action}`, { reason: 'Admin action' });
      if (ok) {
        toast.success(`Boost ${action}d`);
        fetchBoosts();
      }
    } catch { toast.error(`Failed to ${action} boost`); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="boosts-admin-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-lg">Boost Management</h2>
        <div className="flex gap-1 bg-card rounded-lg p-1 border border-border">
          {['active', 'pending', 'all'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${statusFilter === s ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`filter-${s}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {boosts.length > 0 ? (
        <div className="space-y-2">
          {boosts.map((boost, i) => (
            <Card key={boost.boost_id} className="bg-card border-border p-4" data-testid={`admin-boost-${i}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-secondary/20 flex items-center justify-center">
                    <Rocket className="w-4 h-4 text-secondary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium capitalize">{boost.target_type} Boost</p>
                    <p className="text-[10px] text-muted-foreground">
                      by {boost.user_handle ? `@${boost.user_handle}` : boost.user_name || 'Unknown'} | {boost.duration} | {new Date(boost.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={
                    boost.status === 'active' ? 'bg-green-500/20 text-green-400' :
                    boost.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-muted/40 text-muted-foreground'
                  }>{boost.status}</Badge>
                  {boost.status === 'pending' && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 text-green-400" onClick={() => handleAction(boost.boost_id, 'approve')} data-testid={`approve-boost-${i}`}>
                        <CheckCircle className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-red-400" onClick={() => handleAction(boost.boost_id, 'deny')} data-testid={`deny-boost-${i}`}>
                        <XCircle className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-card border-border p-6 text-center" data-testid="no-admin-boosts">
          <Rocket className="w-8 h-8 mx-auto mb-2 text-muted-foreground/80" />
          <p className="text-sm text-muted-foreground">No boosts found for "{statusFilter}"</p>
        </Card>
      )}
    </div>
  );
}
